import type { ReactNode } from 'react'
import { AlertTriangle, Archive, Ban, Merge, Pencil, Play, ShieldAlert, Users } from 'lucide-react'

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { useCan, useMe } from '@/features/access/use-permissions'

import type { Customer } from '@/types'

/* One actions menu for a customer — the table row's "…" and the side panel's
   "⋮" both render this, so they always offer the same things in the same
   order: everyday actions first, the hard-to-undo ones last and in red (no
   separators). Permissions are checked here: Ban, Merge and Archive need
   "Manage customer records"; Erase is the owner's (the API enforces both). */

export interface CustomerActions {
  /** Row only — the panel already is the profile. */
  onView?: (customer: Customer) => void
  onEdit: (customer: Customer) => void
  onStartSession: (customer: Customer) => void
  onFlag: (customer: Customer) => void
  onUnflag: (customer: Customer) => void
  onMerge?: (customer: Customer) => void
  onBan: (customer: Customer) => void
  onUnban: (customer: Customer) => void
  onArchive: (customer: Customer) => void
  onErase?: (customer: Customer) => void
}

interface CustomerActionsMenuProps {
  customer: Customer
  actions: CustomerActions
  /** The button that opens it (rendered asChild). */
  trigger: ReactNode
}

export function CustomerActionsMenu({ customer, actions: a, trigger }: CustomerActionsMenuProps) {
  const canManage = useCan('MANAGE_CUSTOMERS')
  const isOwner = useMe().data?.role === 'OWNER'
  // The row sits inside a clickable table row: a menu pick must not also select it.
  const pick = (fn: (c: Customer) => void) => (e: React.MouseEvent) => {
    e.stopPropagation()
    fn(customer)
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44" data-customer-actions>
        {a.onView && (
          <DropdownMenuItem className="gap-1.5" onClick={pick(a.onView)}>
            <Users className="h-4 w-4" />View profile
          </DropdownMenuItem>
        )}
        <DropdownMenuItem className="gap-1.5" onClick={pick(a.onEdit)}>
          <Pencil className="h-4 w-4" />Edit
        </DropdownMenuItem>
        <DropdownMenuItem className="gap-1.5" onClick={pick(a.onStartSession)}>
          <Play className="h-4 w-4" />Start session
        </DropdownMenuItem>
        {customer.status === 'FLAGGED' ? (
          <DropdownMenuItem className="gap-1.5 text-amber-600 focus:text-amber-600" onClick={pick(a.onUnflag)}>
            <AlertTriangle className="h-4 w-4" />Unflag
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem className="gap-1.5 text-amber-600 focus:text-amber-600" onClick={pick(a.onFlag)}>
            <AlertTriangle className="h-4 w-4" />Flag
          </DropdownMenuItem>
        )}
        {canManage && a.onMerge && (
          <DropdownMenuItem className="gap-1.5" onClick={pick(a.onMerge)}>
            <Merge className="h-4 w-4" />Merge with…
          </DropdownMenuItem>
        )}
        {canManage && (customer.status === 'BANNED' ? (
          <DropdownMenuItem className="gap-1.5 text-muted-foreground" onClick={pick(a.onUnban)}>
            <Ban className="h-4 w-4" />Unban
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem className="gap-1.5 text-destructive focus:text-destructive" onClick={pick(a.onBan)}>
            <Ban className="h-4 w-4" />Ban
          </DropdownMenuItem>
        ))}
        {canManage && (
          <DropdownMenuItem className="gap-1.5 text-destructive focus:text-destructive" onClick={pick(a.onArchive)}>
            <Archive className="h-4 w-4" />Archive
          </DropdownMenuItem>
        )}
        {isOwner && a.onErase && (
          <DropdownMenuItem className="gap-1.5 text-destructive focus:text-destructive" onClick={pick(a.onErase)}>
            <ShieldAlert className="h-4 w-4" />Erase data…
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
